import type { CharacterSkin } from '@shared/protocol';
import { characterApi } from '@/characters/characterApi';

// ─── Default (starter) character — single source of truth ────────────────────
// The app's universal default look is the admin-configured STARTER character
// (e.g. "Spark") — a real cosmetic with transparent art. Everything that needs
// a fallback when the player has NO equipped character pulls from here instead
// of the bare red_*.png climber (whose baked opaque bg tints into a solid box):
// home/profile avatar, friends list, offline + server bots, and the in-match
// Pixi default textures. Fetched once and cached process-wide.

let starterSkinCache: CharacterSkin | null = null;
let inflight: Promise<CharacterSkin | null> | null = null;

/** Synchronously read the cached starter skin (null until first load resolves). */
export function peekStarterSkin(): CharacterSkin | null {
  return starterSkinCache;
}

/** Resolve the default starter character. Picks the admin-flagged starter, then
 * falls back to the player's equipped/owned art so an old server (no isStarter
 * flag) or a partial fetch still avoids the default-PNG box. Cached + de-duped. */
export async function loadStarterSkin(): Promise<CharacterSkin | null> {
  if (starterSkinCache) return starterSkinCache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { items } = await characterApi.list();
      const c =
        items.find((x) => x.isStarter) ??
        items.find((x) => x.equipped) ??
        items.find((x) => x.owned) ??
        items[0];
      if (!c) return null;
      starterSkinCache = {
        id: c.id,
        name: c.name,
        idleUrl: c.idleUrl,
        climbUrl: c.climbUrl,
        fallUrl: c.fallUrl,
        winUrl: c.winUrl,
        idleFrames: c.idleFrames,
        climbFrames: c.climbFrames,
        fallFrames: c.fallFrames,
        winFrames: c.winFrames,
        stompPower: c.stompPower,
        tint: c.tint,
        rarity: c.rarity,
      };
      return starterSkinCache;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
